import React from 'react';

import BoxIcon from '@v2/components/common/products/modals/box-icon';
import ProductLine from '@components/common/builder/box-product-line';
import OOSProductLine from '@components/common/builder/box-oos-product-line';
import { useIsSwapping } from '@components/common/products/utils';

import styles, { loaderCss } from '@v2/components/common/builder/box.styles';
import ImgBox from '@img/shop/build-bundle.png';
import {
    useActiveBox, useActiveBoxItemsCount, useActiveBoxPercentCompleted, useIsActiveBoxCategoryCountAllowed,
    useIsActiveBoxCountAllowed, useIsInitialBoxChanged
} from '@app/store/cart.selectors';

function BoxProgress() {
    const box = useActiveBox();
    const boxCount = useActiveBoxItemsCount();
    const percentCompleted = useActiveBoxPercentCompleted();

    return (
        <table className={`box-blueprint-progress ${percentCompleted === 100 ? 'allowed' : ''}`}>
            <tbody>
                <tr>
                    <td className="progress-text">
                        {
                            !boxCount
                                ?
                                <>
                                    Add at least <span className={'color-red'}>{box.blueprint.minItems}</span> more items
                                </>
                                :
                                boxCount < box.blueprint.minItems
                                    ?
                                    <>
                                        Add <span className={'color-red'}>{box.blueprint.minItems - boxCount}</span> more&nbsp;
                                        {box.blueprint.minItems - boxCount === 1 ? 'item' : 'items'}
                                    </>
                                    :

                                    <span className={'progress-text-count'}>Your bundle is ready!</span>
                        }
                    </td>
                </tr>
                <tr>
                    <td colSpan={2}>
                        <div className="progress-bar">
                            <div className="progress-bar-fill"
                                style={{ width: `${percentCompleted}%` }}></div>
                        </div>
                    </td>
                </tr>
            </tbody>
        </table>
    );
}

function BoxProducts({ processing, resume, setAddedCallback }: { processing: boolean, resume: Function, setAddedCallback: Function }) {
    const box = useActiveBox();

    return (
        <>
            <div className="products">
                <table className="products">
                    <tbody>
                        {box && box.editing && box.oosLines && Object.keys(box.oosLines).map((key: string) =>
                            <OOSProductLine line={box.oosLines[parseInt(key)]} />
                        )}
                        {box && box.lines && Object.keys(box.lines).map((key: string) =>
                            <ProductLine
                                line={box.lines[parseInt(key)]}
                                key={key}
                                editable={!processing}
                                setAddedCallback={setAddedCallback} />
                        )}
                    </tbody>
                    <tfoot>
                        <tr>
                            <td className="total">TOTAL</td>
                            <td></td>
                            <td className="price">
                                {box.price
                                    ?
                                    <>
                                        <span className="no-discount">
                                            &#36;{box.priceDefault
                                                ? box.priceDefault.toFixed(2)
                                                : (box.price / (100 - box.blueprint.discount) * 100).toFixed(2)}
                                        </span>
                                        <span className="current">&#36;{box.price.toFixed(2)}</span>
                                    </>
                                    :
                                    <span>{box.price.toFixed(2)}</span>
                                }
                            </td>
                        </tr>
                    </tfoot>
                </table>
            </div>
        </>
    );
}

function Cart(
    { isLogged, resume, showSizeScreen, onAddBox, setAddedCallback, processing, isEditing }:
        {
            isLogged: boolean, resume: Function, showSizeScreen: Function, onAddBox: Function,
            setAddedCallback: Function, processing: boolean, isEditing?: boolean
        }
) {
    const box = useActiveBox();
    const boxChanged = useIsInitialBoxChanged();
    const boxCount = useActiveBoxItemsCount();
    const boxCategoriesCountAllowed = useIsActiveBoxCategoryCountAllowed();
    const boxCountAllowed = useIsActiveBoxCountAllowed();
    const isSwapping = useIsSwapping();
    const actionDisabled = !boxCountAllowed || processing || !boxCategoriesCountAllowed || !boxChanged;

    return (
        <div className={`${styles.boxContainer} ${boxCount === 0 ? 'empty' : null}`}>
            {box ?
                <>
                    <div className="box-content-wrap">
                        <h4>Your bundle ({boxCount})</h4>
                        <div className="box-content">
                            <div className="box-info">
                                <div className="blueprint">
                                    <BoxIcon type={box.blueprint.type} /><b>{box.blueprint.name}</b> | {box.blueprint.minItems} items
                                </div>
                                <div className="change-size">
                                    <button
                                        disabled={processing}
                                        className={"button secondary link"}
                                        onClick={() => showSizeScreen()}>Change size</button>
                                </div>
                            </div>
                            <BoxProgress />
                            <div className="products-empty">
                                <img loading="lazy" src={ImgBox} alt={"Build A Bundle"} />
                                <div>Add items by clicking on the plus signs below products</div>
                            </div>
                            <BoxProducts processing={processing} resume={resume} setAddedCallback={setAddedCallback} />
                        </div>
                    </div>
                    {isSwapping &&
                        <div className="swap-message">
                            You are currently replacing a product
                        </div>
                    }
                    {box && box.oosLines && Object.keys(box.oosLines).length > 0 && box.editing &&
                        <div className="oos-message">
                            Please add an item to replace the out of stock item in the bundle
                        </div>
                    }
                    <div className="action-button-wrap">
                        <button
                            tabIndex={0}
                            className={`button action ${loaderCss}`}
                            disabled={actionDisabled}
                            onClick={() => onAddBox()}>
                            <span className={`loader  ${processing ? 'active' : ''}`}></span>
                            {(box.editing || isEditing) ? 'Save Changes' : (isSwapping ? 'Add to replacements' : 'ADD TO CART')}
                        </button>
                    </div>
                </> :
                <></>
            }
        </div>
    );
}

export default React.memo(Cart);